import React, { useState } from 'react';
import { Globe, Instagram, Facebook, MapPin, CheckCircle, AlertCircle, TrendingUp, Award, ArrowRight, Mail, Lock } from 'lucide-react';

const BusinessAuditTool = () => {
  const [step, setStep] = useState('input');
  const [businessData, setBusinessData] = useState({
    name: '',
    website: '',
    instagram: '',
    facebook: '',
    googleBusiness: '',
    industry: '',
    placeId: ''
  });
  const [auditResults, setAuditResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const GOOGLE_API_KEY = 'AIzaSyChMk2yDVrpnnshuzs-yWnguj4eFuKytHY';

  const handleInputChange = async (field, value) => {
    setBusinessData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'name' && value.length > 2) {
      try {
        const response = await fetch(
          `https://places.googleapis.com/v1/places:searchText`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': GOOGLE_API_KEY,
              'X-Goog-FieldMask': 'places.displayName,places.id,places.formattedAddress,places.websiteUri,places.googleMapsUri'
            },
            body: JSON.stringify({
              textQuery: value,
              maxResultCount: 5
            })
          }
        );

        const data = await response.json();
        if (data.places && data.places.length > 0) {
          setPredictions(data.places);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      } catch (error) {
        console.error('Error fetching places:', error);
        setPredictions([]);
        setShowPredictions(false);
      }
    } else if (field === 'name' && value.length <= 2) {
      setShowPredictions(false);
      setPredictions([]);
    }
  };

  const selectPlace = (place) => {
    setShowPredictions(false);
    setBusinessData(prev => ({
      ...prev,
      name: place.displayName?.text || prev.name,
      website: place.websiteUri || prev.website,
      googleBusiness: place.googleMapsUri || prev.googleBusiness,
      placeId: place.id
    }));
  };

  const generateAudit = async () => {
  setStep('analyzing');
  setProgress(0);
  setCurrentAnalysisStep('Initializing comprehensive audit...');

  // Analysis step messages
  const analysisSteps = [
    { progress: 20, message: `Analyzing ${businessData.name}...` },
    { progress: 40, message: 'Scanning website and social presence...' },
    { progress: 60, message: 'Reviewing local SEO and reviews...' },
    { progress: 80, message: 'Generating personalized recommendations...' },
    { progress: 95, message: 'Finalizing your audit report...' }
  ];

  let currentStep = 0;
  let smoothProgress = 0;

  // Smooth progress bar that updates every 100ms
  const progressInterval = setInterval(() => {
    smoothProgress += 0.30; // Increment smoothly
    
    // Update message when hitting step thresholds
    if (currentStep < analysisSteps.length && smoothProgress >= analysisSteps[currentStep].progress) {
      setCurrentAnalysisStep(analysisSteps[currentStep].message);
      currentStep++;
    }
    
    // Cap at 97% until API completes
    if (smoothProgress >= 97) {
      smoothProgress = 97;
      clearInterval(progressInterval);
    }
    
    setProgress(smoothProgress);
  }, 100);

  try {
    // API call runs in parallel with progress bar
    const response = await fetch('/api/generate-audit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(businessData)
    });

    const auditData = await response.json();

    // Clear interval and complete
    clearInterval(progressInterval);
    setProgress(100);
    setCurrentAnalysisStep('Audit complete!');
    
    await new Promise(resolve => setTimeout(resolve, 500));
    setAuditResults(auditData);
    setStep('results');

  } catch (error) {
    console.error('Audit error:', error);
    clearInterval(progressInterval);
    setProgress(100);
    await new Promise(resolve => setTimeout(resolve, 500));
    setStep('results');
  }
};

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          businessData: businessData,
          auditResults: auditResults
        })
      });

      if (response.ok) {
        console.log('✅ Email sent successfully');
      }
    } catch (error) {
      console.error('Email error:', error);
    }

    setIsSubmitting(false);
    setIsUnlocked(true);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e'; // Green
    if (score >= 51) return '#eab308'; // Yellow
    return '#ef4444'; // Red
  };

  const ScoreCircle = ({ score, label }) => {
    const color = getScoreColor(score);
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: `conic-gradient(${color} ${score * 3.6}deg, #1a1a1a ${score * 3.6}deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 10px',
          border: `2px solid ${color}`
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: '#000000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 'bold',
            color: color
          }}>
            {score}
          </div>
        </div>
        <p style={{ fontWeight: '600', color: '#ffffff', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px' }}>{label}</p>
      </div>
    );
  };

  if (step === 'input') {
    return (
      <div style={{ minHeight: '100vh', background: '#000000', padding: '60px 20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', background: '#000000', border: '1px solid #222' }}>
          
          <div style={{ background: '#000000', color: 'white', padding: '60px 40px', textAlign: 'center', borderBottom: '1px solid #222' }}>
            <h2 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '400', letterSpacing: '4px', textTransform: 'uppercase', margin: '0 0 30px 0' }}>Pixel Money</h2>
            <h1 style={{ fontSize: '42px', fontWeight: '300', marginBottom: '20px', letterSpacing: '1px', color: '#ffffff' }}>
              Free Digital Audit
            </h1>
            <p style={{ fontSize: '16px', color: '#888', maxWidth: '500px', margin: '0 auto', lineHeight: '1.7', fontWeight: '300' }}>
              Get a comprehensive AI-powered analysis of your digital presence.
            </p>
          </div>

          <div style={{ padding: '50px 40px', background: '#000000' }}>
            <div style={{ marginBottom: '25px', position: 'relative' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                Business Name *
              </label>
              <input
                type="text"
                value={businessData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Start typing your business name..."
                autoComplete="off"
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  fontWeight: '300'
                }}
                onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                onBlur={(e) => {
                  e.target.style.borderBottomColor = '#333';
                  setTimeout(() => setShowPredictions(false), 300);
                }}
              />
              {showPredictions && predictions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#0a0a0a',
                  border: '1px solid #333',
                  marginTop: '5px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000
                }}>
                  {predictions.map((place, index) => (
                    <div
                      key={place.id || index}
                      onMouseDown={() => selectPlace(place)}
                      style={{
                        padding: '14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #222',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#111'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      <div style={{ marginBottom: '4px' }}>{place.displayName?.text || 'Unknown'}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{place.formattedAddress || ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                <Globe size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Website URL *
              </label>
              <input
                type="url"
                value={businessData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://yourwebsite.com"
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  fontWeight: '300'
                }}
                onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                onBlur={(e) => e.target.style.borderBottomColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                <Instagram size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Instagram Handle
              </label>
              <input
                type="text"
                value={businessData.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                placeholder="@yourbusiness"
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  fontWeight: '300'
                }}
                onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                onBlur={(e) => e.target.style.borderBottomColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                <Facebook size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Facebook Page
              </label>
              <input
                type="text"
                value={businessData.facebook}
                onChange={(e) => handleInputChange('facebook', e.target.value)}
                placeholder="facebook.com/yourpage"
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  fontWeight: '300'
                }}
                onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                onBlur={(e) => e.target.style.borderBottomColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                <MapPin size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                Google Business Profile
              </label>
              <input
                type="text"
                value={businessData.googleBusiness}
                onChange={(e) => handleInputChange('googleBusiness', e.target.value)}
                placeholder="Google Business Profile URL"
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: '#ffffff',
                  outline: 'none',
                  fontWeight: '300'
                }}
                onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
                onBlur={(e) => e.target.style.borderBottomColor = '#333'}
              />
            </div>

            <div style={{ marginBottom: '45px' }}>
              <label style={{ display: 'block', fontWeight: '400', marginBottom: '8px', color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                Industry *
              </label>
              <select
                value={businessData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '14px 0', 
                  border: 'none',
                  borderBottom: '1px solid #333',
                  background: 'transparent',
                  fontSize: '16px',
                  color: businessData.industry ? '#ffffff' : '#666',
                  cursor: 'pointer',
                  outline: 'none',
                  fontWeight: '300'
                }}
              >
                <option value="" style={{ background: '#000', color: '#666' }}>Select Industry</option>
                <option value="restaurant" style={{ background: '#000', color: '#fff' }}>Restaurant/Food Service</option>
                <option value="retail" style={{ background: '#000', color: '#fff' }}>Retail/E-commerce</option>
                <option value="services" style={{ background: '#000', color: '#fff' }}>Professional Services</option>
                <option value="health" style={{ background: '#000', color: '#fff' }}>Health & Wellness</option>
                <option value="beauty" style={{ background: '#000', color: '#fff' }}>Beauty & Salon</option>
                <option value="real-estate" style={{ background: '#000', color: '#fff' }}>Real Estate</option>
                <option value="automotive" style={{ background: '#000', color: '#fff' }}>Automotive</option>
                <option value="other" style={{ background: '#000', color: '#fff' }}>Other</option>
              </select>
            </div>

            <button
              onClick={generateAudit}
              disabled={!businessData.name || !businessData.website || !businessData.industry}
              style={{
                width: '100%',
                padding: '18px',
                background: businessData.name && businessData.website && businessData.industry ? '#ffffff' : '#111',
                color: businessData.name && businessData.website && businessData.industry ? '#000000' : '#444',
                border: businessData.name && businessData.website && businessData.industry ? '1px solid #ffffff' : '1px solid #222',
                fontSize: '11px',
                fontWeight: '400',
                cursor: businessData.name && businessData.website && businessData.industry ? 'pointer' : 'not-allowed',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              Generate Audit <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'analyzing') {
    return (
      <div style={{ minHeight: '100vh', background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ maxWidth: '700px', width: '100%', background: '#000000', border: '1px solid #222', padding: '60px 40px', textAlign: 'center' }}>
          
          <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'center' }}>
            <img 
              src="https://static.wixstatic.com/media/2a4170_f015319b3e6c426d8140ea267b926d51~mv2.gif" 
              alt="Analyzing"
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>

          <h2 style={{ fontSize: '24px', fontWeight: '300', marginBottom: '10px', color: '#ffffff', letterSpacing: '1px' }}>
            Analyzing {businessData.name}
          </h2>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '40px', fontWeight: '300' }}>
            Running comprehensive digital presence audit with AI web search
          </p>
          
          <div style={{ background: '#000000', border: '1px solid #222', padding: '20px', marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontWeight: '400', color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>Progress</span>
              <span style={{ fontWeight: '300', color: '#ffffff', fontSize: '14px' }}>{Math.round(progress)}%</span>
            </div>
            <div style={{ height: '2px', background: '#222', borderRadius: '2px' }}>
              <div style={{ 
                height: '100%', 
                background: '#ffffff', 
                width: `${progress}%`,
                transition: 'width 0.15s linear',
                borderRadius: '2px'
              }} />
            </div>
          </div>

          <div style={{ background: '#000000', border: '1px solid #222', padding: '25px', minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontSize: '13px', color: '#aaa', fontWeight: '300', margin: 0, textAlign: 'center', letterSpacing: '0.5px', lineHeight: '1.6' }}>
              {currentAnalysisStep}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // RESULTS PAGE
  if (!auditResults) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#000000', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif', position: 'relative' }}>
      
      {!isUnlocked && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{ background: '#000', border: '2px solid #fff', padding: '40px 30px', maxWidth: '500px', width: '100%', textAlign: 'center' }}>
            <Lock size={48} style={{ color: '#fff', marginBottom: '20px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: '300', color: '#fff', marginBottom: '15px' }}>
              Unlock Your Full Audit Report
            </h2>
            <p style={{ fontSize: '14px', color: '#888', marginBottom: '30px', lineHeight: '1.7' }}>
              Enter your email to view your complete audit. Your detailed PDF report will be emailed within 24 hours.
            </p>

            <form onSubmit={handleEmailSubmit} style={{ maxWidth: '400px', margin: '0 auto' }}>
  <input
    type="email"
    value={userEmail}
    onChange={(e) => setUserEmail(e.target.value)}
    placeholder="your@email.com"
    required
    style={{
      width: '100%',
      padding: '18px',  // Changed from 16px to match button
      marginBottom: '20px',  // Reduced from 25px for tighter spacing
      border: '1px solid #333',
      background: '#000',
      color: '#fff',
      fontSize: '16px',
      outline: 'none',
      boxSizing: 'border-box'  // ADDED - ensures border is included in width
    }}
  />
  <button
    type="submit"
    disabled={isSubmitting || !userEmail}
    style={{
      width: '100%',
      padding: '18px',
      background: '#fff',
      color: '#000',
      border: 'none',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxSizing: 'border-box'  // ADDED for consistency
    }}
  >
    {isSubmitting ? 'Unlocking...' : (
      <>
        <Mail size={16} />
        Unlock Report
      </>
    )}
  </button>
</form>

            <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
              PDF report will be emailed within 24 hours
            </p>
          </div>
        </div>
      )}

      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* SCORES - MOBILE OPTIMIZED */}
        <div style={{ background: '#000000', border: '1px solid #222', padding: '30px 20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <span style={{ color: '#888', fontSize: '10px', fontWeight: '400', letterSpacing: '2px', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>Pixel Money</span>
              <h1 style={{ fontSize: '28px', fontWeight: '300', color: '#ffffff', marginBottom: '5px', wordBreak: 'break-word' }}>
                {businessData.name}
              </h1>
              <p style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>Digital Audit Report</p>
            </div>
            <button
              onClick={() => { setStep('input'); setAuditResults(null); setIsUnlocked(false); setUserEmail(''); setBusinessData({ name: '', website: '', instagram: '', facebook: '', googleBusiness: '', industry: '', placeId: '' }); }}
              style={{ padding: '10px 20px', background: '#000', color: '#fff', border: '1px solid #fff', cursor: 'pointer', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', whiteSpace: 'nowrap' }}
            >
              New Audit
            </button>
          </div>

          {/* MOBILE OPTIMIZED SCORE GRID */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', 
            gap: '20px', 
            marginTop: '30px',
            justifyItems: 'center'
          }}>
            <ScoreCircle score={auditResults.overallScore} label="Overall" />
            <ScoreCircle score={auditResults.seoScore} label="SEO" />
            <ScoreCircle score={auditResults.socialScore} label="Social" />
            <ScoreCircle score={auditResults.localScore} label="Local" />
          </div>
        </div>

        {/* FULL AUDIT DETAILS */}
        <div style={{ filter: isUnlocked ? 'none' : 'blur(10px)', pointerEvents: isUnlocked ? 'auto' : 'none', transition: 'filter 0.5s', userSelect: isUnlocked ? 'auto' : 'none' }}>
          
          {/* PRIORITY ACTIONS */}
          <div style={{ background: '#000000', border: '1px solid #ffffff', padding: '30px 20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '10px' }}>
              <Award size={24} style={{ color: '#ffffff', marginRight: '12px' }} />
              <h2 style={{ fontSize: '18px', fontWeight: '300', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '2px' }}>Priority Actions</h2>
            </div>
            {auditResults.priority && auditResults.priority.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px', paddingBottom: '15px', borderBottom: i < auditResults.priority.length - 1 ? '1px solid #222' : 'none' }}>
                <div style={{ 
                  minWidth: '28px', 
                  height: '28px', 
                  background: '#ffffff', 
                  color: '#000000', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: '600',
                  marginRight: '15px',
                  fontSize: '14px',
                  flexShrink: 0
                }}>
                  {i + 1}
                </div>
                <p style={{ fontSize: '14px', color: '#cccccc', lineHeight: '1.6', marginTop: '4px', fontWeight: '300' }}>{item}</p>
              </div>
            ))}
            
            <div style={{ marginTop: '30px', padding: '20px', background: '#000000', border: '1px solid #333' }}>
              <h3 style={{ fontSize: '10px', fontWeight: '400', color: '#888', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '2px' }}>Estimated Value</h3>
              <p style={{ fontSize: '20px', fontWeight: '300', color: '#ffffff', letterSpacing: '0.5px' }}>{auditResults.estimatedValue}</p>
            </div>
          </div>

          {/* SEO ANALYSIS */}
{auditResults.seoFindings && (
  <div style={{ background: '#000000', border: '1px solid #222', padding: '30px 20px', marginBottom: '20px' }}>
    <div style={{ borderLeft: '2px solid #ffffff', paddingLeft: '15px', marginBottom: '25px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '300', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '2px' }}>SEO Analysis</h2>
    </div>
    
    <div style={{ marginBottom: '25px' }}>
      <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#22c55e', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
        <CheckCircle size={14} style={{ color: '#22c55e' }} />
        Strengths
      </h3>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {auditResults.seoFindings.strengths && auditResults.seoFindings.strengths.map((item, i) => (
          <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '400', fontSize: '13px', wordBreak: 'break-word' }}>✓ {item}</li>
        ))}
      </ul>
    </div>
    
    <div style={{ marginBottom: '25px' }}>
      <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#ef4444', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
        <AlertCircle size={14} style={{ color: '#ef4444' }} />
        Issues Found
      </h3>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {auditResults.seoFindings.weaknesses && auditResults.seoFindings.weaknesses.map((item, i) => (
          <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '400', fontSize: '13px', wordBreak: 'break-word' }}>✗ {item}</li>
        ))}
      </ul>
    </div>
    
    <div>
      <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#f59e0b', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
        <TrendingUp size={14} style={{ color: '#f59e0b' }} />
        Recommendations
      </h3>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {auditResults.seoFindings.recommendations && auditResults.seoFindings.recommendations.map((item, i) => (
          <li key={i} style={{ padding: '14px', marginBottom: '10px', background: '#000', border: '1px solid #222', color: '#ffffff', fontWeight: '400', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
            → {item}
          </li>
        ))}
      </ul>
    </div>
  </div>
)}

          {/* SOCIAL MEDIA ANALYSIS */}
          {auditResults.socialFindings && (
            <div style={{ background: '#000000', border: '1px solid #222', padding: '30px 20px', marginBottom: '20px' }}>
              <div style={{ borderLeft: '2px solid #ffffff', paddingLeft: '15px', marginBottom: '25px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '300', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '2px' }}>Social Media Analysis</h2>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#22c55e', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <CheckCircle size={14} style={{ color: '#22c55e' }} />
                  Strengths
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.socialFindings.strengths && auditResults.socialFindings.strengths.map((item, i) => (
                    <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '300', fontSize: '13px', wordBreak: 'break-word' }}>✓ {item}</li>
                  ))}
                </ul>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#ef4444', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <AlertCircle size={14} style={{ color: '#ef4444' }} />
                  Issues Found
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.socialFindings.weaknesses && auditResults.socialFindings.weaknesses.map((item, i) => (
                    <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '300', fontSize: '13px', wordBreak: 'break-word' }}>✗ {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#f59e0b', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <TrendingUp size={14} style={{ color: '#f59e0b' }} />
                  Recommendations
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.socialFindings.recommendations && auditResults.socialFindings.recommendations.map((item, i) => (
                    <li key={i} style={{ padding: '14px', marginBottom: '10px', background: '#000', border: '1px solid #222', color: '#ffffff', fontWeight: '300', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                      → {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* LOCAL SEO ANALYSIS */}
          {auditResults.localFindings && (
            <div style={{ background: '#000000', border: '1px solid #222', padding: '30px 20px', marginBottom: '20px' }}>
              <div style={{ borderLeft: '2px solid #ffffff', paddingLeft: '15px', marginBottom: '25px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '300', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '2px' }}>Local SEO Analysis</h2>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#22c55e', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <CheckCircle size={14} style={{ color: '#22c55e' }} />
                  Strengths
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.localFindings.strengths && auditResults.localFindings.strengths.map((item, i) => (
                    <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '300', fontSize: '13px', wordBreak: 'break-word' }}>✓ {item}</li>
                  ))}
                </ul>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#ef4444', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <AlertCircle size={14} style={{ color: '#ef4444' }} />
                  Issues Found
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.localFindings.weaknesses && auditResults.localFindings.weaknesses.map((item, i) => (
                    <li key={i} style={{ padding: '12px 0', color: '#ffffff', borderBottom: '1px solid #1a1a1a', fontWeight: '300', fontSize: '13px', wordBreak: 'break-word' }}>✗ {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 style={{ fontSize: '12px', fontWeight: '400', color: '#f59e0b', marginBottom: '15px', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '2px', flexWrap: 'wrap', gap: '8px' }}>
                  <TrendingUp size={14} style={{ color: '#f59e0b' }} />
                  Recommendations
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {auditResults.localFindings.recommendations && auditResults.localFindings.recommendations.map((item, i) => (
                    <li key={i} style={{ padding: '14px', marginBottom: '10px', background: '#000', border: '1px solid #222', color: '#ffffff', fontWeight: '300', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                      → {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* CTA */}
          <div style={{ background: '#000000', border: '1px solid #ffffff', padding: '40px 30px', textAlign: 'center', color: 'white' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '300', marginBottom: '15px', letterSpacing: '1px' }}>Ready to Turn Pixels Into Profit?</h2>
            <p style={{ fontSize: '14px', marginBottom: '30px', color: '#888', maxWidth: '600px', margin: '0 auto 30px', fontWeight: '300', lineHeight: '1.6' }}>
              Let Pixel Money implement these changes and build your revenue-producing digital presence.
            </p>
            <button style={{
              padding: '16px 35px',
              background: '#ffffff',
              color: '#000000',
              border: '1px solid #ffffff',
              fontSize: '11px',
              fontWeight: '400',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '3px',
              transition: 'all 0.3s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onClick={() => window.open('https://www.pixelmoney.ai/book-online', '_blank')}
            >
              Book Free Consultation <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  return <BusinessAuditTool />;
}